// Secure YouTube player for the learner course-player. Enforces the same
// anti-skip / auto-complete rules as the LMS HLS player using the YouTube
// IFrame API, custom controls (native controls hidden), and progress
// heartbeats posted to the existing /video-progress endpoint.
//
// Limitation acknowledgement: YouTube's iframe cannot be fully sandboxed —
// a determined user can still open the same video directly on YouTube.
// For strict certificate-eligibility use LMS-hosted HLS instead.
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Play, Pause, Volume2, VolumeX, Maximize, CheckCircle2, Captions, CaptionsOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const SEEK_TOLERANCE_S = 5;
const HEARTBEAT_MS = 12000;   // spec: every 10-15s

let _ytApiPromise = null;
function loadYouTubeApi() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (_ytApiPromise) return _ytApiPromise;
  _ytApiPromise = new Promise((resolve) => {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === "function") { try { prev(); } catch { /* ignore */ } }
      resolve(window.YT);
    };
  });
  return _ytApiPromise;
}

function extractVideoId(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.replace(/^\//, "").split("?")[0];
    const v = u.searchParams.get("v");
    if (v) return v;
  } catch { /* ignore */ }
  return null;
}

const fmt = (s) => {
  if (!Number.isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60), r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, "0")}`;
};

export function YouTubeSecurePlayer({ lessonId, payload, onCompleted, watermarks, autoPlay = false, onPlayStarted }) {
  const holderRef = useRef(null);           // <div> that YT.Player replaces
  const containerRef = useRef(null);        // wrap that owns fullscreen + overlays
  const playerRef = useRef(null);           // YT.Player instance
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [capsOn, setCapsOn] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [resumed, setResumed] = useState(false);
  const [canMarkComplete, setCanMarkComplete] = useState(!!payload?.can_mark_complete);
  const [markingComplete, setMarkingComplete] = useState(false);
  const completedRef = useRef(!!payload?.is_completed);
  // Snapshot of the mount-time completion so heartbeat auto-advance
  // doesn't fire the moment we open an already-completed lesson.
  const wasCompletedAtMountRef = useRef(!!payload?.is_completed);
  // Separate from completedRef: this tracks whether we've fired the
  // onCompleted() callback (auto-advance / parent refresh) during THIS
  // mount. Fires exactly once per mount from STATE_ENDED or from a
  // FRESH heartbeat completion — a re-watch never triggers it on the
  // very first tick.
  const onCompletedFiredRef = useRef(false);
  const fireCompletedOnce = () => {
    if (onCompletedFiredRef.current) return;
    onCompletedFiredRef.current = true;
    if (typeof onCompleted === "function") onCompleted();
  };
  // Corner-band width comes from the backend (Super Admin tunable
  // 0-100 %). Clamp defensively so a rogue value can never render as
  // a negative width; 0 % means the bands disappear entirely (YouTube
  // branding fully visible), 100 % means both halves cover the whole
  // top/bottom strip (centre gap gone).
  const coverPct = Math.min(100, Math.max(0,
    Number(payload?.security?.youtube_cover_band_percent) || 32,
  ));

  const handleMarkComplete = useCallback(async () => {
    setMarkingComplete(true);
    try {
      const { data } = await api.post(`/learner/lessons/${lessonId}/mark-video-complete`);
      if (data?.data?.is_completed) {
        completedRef.current = true;
        setCanMarkComplete(false);
        toast.success("Marked as complete.");
        if (typeof onCompleted === "function") onCompleted();
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setMarkingComplete(false); }
  }, [lessonId, onCompleted]);

  // Anti-skip trackers (refs so nothing churns re-renders)
  const maxWatchedRef = useRef(Number(payload?.max_watched_position || 0));
  const lastValidRef = useRef(Number(payload?.max_watched_position || 0));
  const uniqueRef = useRef(Number(payload?.unique_watched_seconds || 0));
  const lastTickRef = useRef(0);
  const programmaticSeekRef = useRef(false);
  const programmaticTimerRef = useRef(null);
  const lastWarnAtRef = useRef(0);
  // Resume machinery. `resumeTargetRef` is the seconds we must jump to on
  // first load; `resumeAppliedRef` becomes true only after we have polled
  // the YT player and confirmed currentTime ≥ target-2. Until then every
  // seek/tick/heartbeat/FF-warning is silenced — YouTube can briefly
  // report currentTime=0 during initial buffering, which would otherwise
  // trip the anti-skip guard and snap the learner back to 0.
  //
  // RE-WATCH FIX: if the lesson is already completed, start from 0
  // (not from max_watched, which is essentially the end of the video).
  // Anti-skip stays safe because maxWatchedRef keeps the full duration
  // → seeking anywhere in the timeline is allowed for the re-watch.
  const _initialResume = payload?.is_completed
    ? 0
    : Number(payload?.max_watched_position || 0);
  const resumeTargetRef = useRef(_initialResume);
  const resumeAppliedRef = useRef(_initialResume <= 3);
  const lessonIdRef = useRef(lessonId);
  useEffect(() => { lessonIdRef.current = lessonId; }, [lessonId]);

  const resumeVerifyRef = useRef(null);

  const startProgrammaticSeek = () => {
    programmaticSeekRef.current = true;
    if (programmaticTimerRef.current) clearTimeout(programmaticTimerRef.current);
    // 2500ms — YouTube's seek + initial segment fetch can take >1s on cold
    // networks; 600ms was too tight and let the second tick trip the guard.
    programmaticTimerRef.current = setTimeout(() => { programmaticSeekRef.current = false; }, 2500);
  };

  // -------- Progress heartbeat (also fires on pause / end / unmount) --------
  const reportProgress = useCallback(async (ct, opts = {}) => {
    const forLesson = lessonId;
    try {
      const { data } = await api.post(`/learner/lessons/${forLesson}/video-progress`, {
        current_time_seconds: Number(ct.toFixed(2)),
        watched_percentage: duration ? Math.min(100, (uniqueRef.current / duration) * 100) : 0,
        max_watched_position: Number(maxWatchedRef.current.toFixed(2)),
        unique_watched_seconds: Number(uniqueRef.current.toFixed(2)),
        video_duration: duration || undefined,
        seek_attempt: !!opts.seekAttempt,
      });
      if (lessonIdRef.current !== forLesson) return;
      if (data?.success === false && data?.message?.toLowerCase().includes("fast forward")) {
        const allowed = Number(data.data?.allowed_position || maxWatchedRef.current || 0);
        startProgrammaticSeek();
        try { playerRef.current?.seekTo(allowed, true); } catch { /* ignore */ }
        // Silently correct during the resume phase — it's not a user
        // fast-forward, just the first heartbeat racing the resume seek.
        if (resumeAppliedRef.current) {
          const now = Date.now();
          if (now - lastWarnAtRef.current > 3000) {
            lastWarnAtRef.current = now;
            toast.warning("Fast forward is disabled. You can rewind only to already watched sections.");
          }
        }
      }
      // Toggle the manual "Mark as complete" button when the learner has
      // watched the manual-gate threshold (default 95 %).
      if (data?.data?.can_mark_complete === true && !completedRef.current) {
        setCanMarkComplete(true);
      }
      if (data?.data?.is_completed && !completedRef.current) {
        completedRef.current = true;
        setCanMarkComplete(false);
        toast.success("Video lesson completed automatically.");
      }
      // Auto-advance ONLY on fresh completions. On a re-watch the very
      // first heartbeat says is_completed=true (it already was), and
      // firing here would jump the learner to the next lesson before
      // they've watched a single frame of the re-watch. STATE_ENDED
      // remains the source of truth for actual "I finished watching
      // this again" events.
      if (data?.data?.is_completed && !wasCompletedAtMountRef.current) {
        fireCompletedOnce();
      }
    } catch { /* silent */ }
  }, [lessonId, duration, onCompleted]);

  // -------- YT API boot + player instantiation --------
  useEffect(() => {
    let cancelled = false;
    const videoId = extractVideoId(payload?.external_url || "");
    if (!videoId || !holderRef.current) return undefined;
    setReady(false);

    loadYouTubeApi().then((YT) => {
      if (cancelled || !holderRef.current) return;
      // Use resumeTargetRef (already 0 for completed re-watches, or
      // the max_watched position for in-progress lessons) instead of
      // maxWatchedRef directly — otherwise a 100 %-completed video
      // would open the player at the very last frame and the learner
      // couldn't re-watch it from the beginning.
      const startAt = Math.max(0, Math.floor(resumeTargetRef.current || 0));
      playerRef.current = new YT.Player(holderRef.current, {
        videoId,
        playerVars: {
          controls: 0,       // hide native controls
          disablekb: 1,      // block YouTube's keyboard shortcuts
          rel: 0,            // don't recommend other channels' videos on pause
          modestbranding: 1,
          playsinline: 1,
          iv_load_policy: 3, // no annotations
          fs: 0,             // block YT's fullscreen button (we do our own)
          start: startAt,
          cc_load_policy: 1, // show closed captions by default
          cc_lang_pref: "en",
          hl: "en",
        },
        events: {
          onReady: (e) => {
            const d = e.target.getDuration() || 0;
            setDuration(d);
            setReady(true);
            // Parent asked us to auto-play (e.g. auto-advance from the
            // previous lesson). Since the learner just finished the
            // previous video (a real user gesture), unmuted playback
            // is virtually always allowed — do NOT reflexively mute.
            // The muted fallback here previously fired after 400 ms
            // regardless of actual state, so buffering (state=3)
            // videos got wrongly muted mid-load. We now wait 2.5 s
            // AND only mute if the player is truly still in an
            // "autoplay was blocked" state (UNSTARTED=-1 or PAUSED=2).
            // BUFFERING(3), PLAYING(1), CUED(5) → leave the audio on.
            if (autoPlay) {
              try {
                e.target.playVideo();
                if (typeof onPlayStarted === "function") onPlayStarted();
              } catch { /* ignore */ }
              setTimeout(() => {
                try {
                  const st = e.target.getPlayerState?.();
                  // -1 UNSTARTED  |  2 PAUSED  → browser blocked the
                  // autoplay. Only in these two states do we drop to
                  // muted playback as a last resort.
                  if (st === -1 || st === 2) {
                    e.target.mute();
                    e.target.playVideo();
                  }
                } catch { /* ignore */ }
              }, 2500);
            }
            if (startAt > 3) {
              // Start-programmatic BEFORE the seek so the tick loop's
              // anti-skip is silenced through the segment fetch.
              startProgrammaticSeek();
              try { e.target.seekTo(startAt, true); } catch { /* ignore */ }
              setResumed(true);
              const mm = Math.floor(startAt / 60);
              const ss = Math.floor(startAt % 60).toString().padStart(2, "0");
              toast.info(`Resuming from where you left off (${mm}:${ss}).`);
              // Poll until YT reports currentTime ≥ target-2, then release
              // the resume-applied gate. YT can briefly return 0 during the
              // initial buffer even after seekTo, which would otherwise
              // trip the anti-skip snap-back-to-zero.
              let tries = 0;
              if (resumeVerifyRef.current) clearInterval(resumeVerifyRef.current);
              resumeVerifyRef.current = setInterval(() => {
                tries += 1;
                const ct = e.target.getCurrentTime?.() || 0;
                if (ct >= startAt - 2) {
                  lastTickRef.current = ct;
                  lastValidRef.current = ct;
                  resumeAppliedRef.current = true;
                  clearInterval(resumeVerifyRef.current); resumeVerifyRef.current = null;
                } else if (tries % 4 === 0) {
                  // Every ~2s, re-issue the seek in case YT dropped it.
                  startProgrammaticSeek();
                  try { e.target.seekTo(startAt, true); } catch { /* ignore */ }
                }
                if (tries > 20) {   // 10s ceiling
                  resumeAppliedRef.current = true;
                  clearInterval(resumeVerifyRef.current); resumeVerifyRef.current = null;
                }
              }, 500);
            } else {
              resumeAppliedRef.current = true;
            }
          },
          onStateChange: (e) => {
            const st = e.data;   // 1=play, 2=pause, 0=ended, 3=buffer
            if (st === 1) setPlaying(true);
            if (st === 2) { setPlaying(false); if (resumeAppliedRef.current) reportProgress(e.target.getCurrentTime()); }
            if (st === 0) {
              setPlaying(false);
              // Force unique to duration when the video actually ends — YT
              // occasionally under-reports the last few tenths of a second.
              if (duration) { uniqueRef.current = Math.max(uniqueRef.current, duration); maxWatchedRef.current = Math.max(maxWatchedRef.current, duration); }
              reportProgress(e.target.getCurrentTime());
              // Belt-and-braces: also POST /mark-video-complete so a lesson
              // that just ended is *guaranteed* to unlock the next one,
              // even if the browser closes before the heartbeat's response
              // is processed. The endpoint is idempotent — a second call
              // on an already-complete lesson is a no-op success.
              if (!completedRef.current) {
                api.post(`/learner/lessons/${lessonId}/mark-video-complete`)
                  .then(({ data }) => {
                    if (data?.data?.is_completed) {
                      completedRef.current = true;
                      setCanMarkComplete(false);
                    }
                  })
                  .catch(() => { /* silent — server may still be catching up */ });
              }
              // ALWAYS trigger auto-advance when the video actually ends,
              // regardless of whether the lesson was already marked
              // completed at mount (e.g. re-watch or restored progress).
              // Without this, learners who returned to a completed
              // lesson to re-watch it would end up staring at a paused
              // final frame with no auto-play of the next lesson.
              fireCompletedOnce();
            }
          },
        },
      });
    });

    return () => {
      cancelled = true;
      // Final heartbeat so exit progress is recorded.
      try {
        const ct = playerRef.current?.getCurrentTime?.() ?? 0;
        // Fire and forget — no await; we're unmounting.
        api.post(`/learner/lessons/${lessonId}/video-progress`, {
          current_time_seconds: Number(ct.toFixed(2)),
          watched_percentage: duration ? Math.min(100, (uniqueRef.current / duration) * 100) : 0,
          max_watched_position: Number(maxWatchedRef.current.toFixed(2)),
          unique_watched_seconds: Number(uniqueRef.current.toFixed(2)),
          video_duration: duration || undefined,
        }).catch(() => {});
      } catch { /* ignore */ }
      if (programmaticTimerRef.current) { clearTimeout(programmaticTimerRef.current); programmaticTimerRef.current = null; }
      if (resumeVerifyRef.current) { clearInterval(resumeVerifyRef.current); resumeVerifyRef.current = null; }
      try { playerRef.current?.destroy?.(); } catch { /* ignore */ }
      playerRef.current = null;
    };
  }, [payload?.external_url, lessonId]);

  // -------- Tick loop: track currentTime, grow unique on natural delta --------
  useEffect(() => {
    if (!ready) return undefined;
    const id = setInterval(() => {
      const p = playerRef.current;
      if (!p || !p.getCurrentTime) return;
      const t = p.getCurrentTime();
      setCurrentTime(t);
      if (!p.getPlayerState || p.getPlayerState() !== 1) return;   // only during play
      // Silence all anti-skip logic until the resume seek has been
      // confirmed applied — YT often reports currentTime=0 for a brief
      // window during initial buffering.
      if (!resumeAppliedRef.current) { lastTickRef.current = t; lastValidRef.current = t; return; }
      const prev = lastTickRef.current || 0;
      const delta = t - prev;
      // Positive small delta = natural playback tick (poll cadence is 500 ms
      // so max delta on smooth playback is ~0.5-0.6 s — 2.5 s covers browser
      // throttling on inactive tabs without letting a real seek pass).
      if (delta > 0 && delta < 2.5) uniqueRef.current += delta;
      if (t > (maxWatchedRef.current || 0)) maxWatchedRef.current = t;
      lastValidRef.current = t;
      lastTickRef.current = t;
      // ANTI-SKIP detection: if the reported currentTime jumps forward more
      // than (SEEK_TOLERANCE_S + last known tick) since the previous tick,
      // AND it's not programmatic, treat as an intentional forward seek.
      if (!programmaticSeekRef.current && delta > (SEEK_TOLERANCE_S + 1)) {
        // Snap back to last valid position
        const target = lastValidRef.current - delta;  // undo the jump
        startProgrammaticSeek();
        try { p.seekTo(Math.max(0, target), true); } catch { /* ignore */ }
        const now = Date.now();
        if (now - lastWarnAtRef.current > 3000) {
          lastWarnAtRef.current = now;
          toast.warning("Fast forward is disabled. You can rewind only to already watched sections.");
        }
        reportProgress(target, { seekAttempt: true });
      }
    }, 500);
    return () => clearInterval(id);
  }, [ready, reportProgress]);

  // -------- Progress heartbeat (12s cadence) --------
  useEffect(() => {
    if (!ready) return undefined;
    const id = setInterval(() => {
      const p = playerRef.current;
      if (!p || !p.getCurrentTime) return;
      if (p.getPlayerState && p.getPlayerState() !== 1) return;  // only while playing
      // Don't heartbeat before the resume seek has confirmed — otherwise
      // the first tick sends currentTime≈0 while max=<resume>, and the
      // second tick can look like a fast-forward when the seek finally
      // lands.
      if (!resumeAppliedRef.current) return;
      reportProgress(p.getCurrentTime());
    }, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [ready, reportProgress]);

  // -------- Custom control handlers --------
  const togglePlay = () => {
    const p = playerRef.current; if (!p) return;
    const st = p.getPlayerState && p.getPlayerState();
    if (st === 1) p.pauseVideo(); else p.playVideo();
  };
  const toggleMute = () => {
    const p = playerRef.current; if (!p) return;
    if (p.isMuted()) { p.unMute(); setMuted(false); } else { p.mute(); setMuted(true); }
  };
  const onScrub = (e) => {
    const p = playerRef.current; if (!p) return;
    const ct = Number(e.target.value);
    // Anti-skip on custom scrub: only allow moving to already-watched territory
    const ceiling = (maxWatchedRef.current || 0) + SEEK_TOLERANCE_S;
    if (ct > ceiling) {
      const now = Date.now();
      if (now - lastWarnAtRef.current > 3000) {
        lastWarnAtRef.current = now;
        toast.warning("Fast forward is disabled. You can rewind only to already watched sections.");
      }
      // Snap the range input back
      setCurrentTime(maxWatchedRef.current || 0);
      startProgrammaticSeek();
      try { p.seekTo(maxWatchedRef.current || 0, true); } catch { /* ignore */ }
      return;
    }
    startProgrammaticSeek();
    try { p.seekTo(ct, true); } catch { /* ignore */ }
    setCurrentTime(ct);
    lastValidRef.current = ct;
  };
  const goFullscreen = async () => {
    const box = containerRef.current;
    if (!box) return;
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (box.requestFullscreen) await box.requestFullscreen();
      else if (box.webkitRequestFullscreen) box.webkitRequestFullscreen();
    } catch { /* ignore */ }
  };

  const watchedPct = duration ? Math.min(100, (uniqueRef.current / duration) * 100) : 0;

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden rounded-2xl border border-slate-200 bg-black"
      data-testid="lesson-youtube-secure"
    >
      {/* Native YT iframe (controls hidden by playerVars) */}
      <div className="aspect-video">
        <div ref={holderRef} className="h-full w-full" data-testid="youtube-iframe-mount" />
      </div>
      {/* Invisible transparent shield over the iframe: swallows clicks that
          would otherwise reach YouTube's remaining tap-target (title bar,
          watch-on-YouTube badge, etc). Bottom band is left free for our
          custom controls. */}
      <div
        className="absolute inset-0 bottom-16 z-10"
        onClick={togglePlay}
        onContextMenu={(e) => e.preventDefault()}
        data-testid="youtube-click-shield"
      />

      {/* Anti-branding bands: split into LEFT + RIGHT corner strips
          (not full width) so YouTube's title (top-left), share/watch
          buttons (top-right), and Watch-on-YouTube wordmark (bottom
          corners) stay hidden — BUT the center gap is left transparent
          so the video's own captions / on-screen text remain readable.
          Width is Super-Admin-tunable (0-100 %) via platform_settings.
          Falls back to 32 % when the setting isn't present, matching
          the historic default. `pointer-events-none` so clicks pass
          through to controls. */}
      <div className="pointer-events-none absolute left-0 top-0 z-[15] h-12 bg-black"
           style={{ width: `${coverPct}%` }}
           aria-hidden data-testid="yt-cover-top-left" />
      <div className="pointer-events-none absolute right-0 top-0 z-[15] h-12 bg-black"
           style={{ width: `${coverPct}%` }}
           aria-hidden data-testid="yt-cover-top-right" />
      <div className="pointer-events-none absolute left-0 bottom-0 z-[15] h-16 bg-black"
           style={{ width: `${coverPct}%` }}
           aria-hidden data-testid="yt-cover-bottom-left" />
      <div className="pointer-events-none absolute right-0 bottom-0 z-[15] h-16 bg-black"
           style={{ width: `${coverPct}%` }}
           aria-hidden data-testid="yt-cover-bottom-right" />

      {/* SIHMT branding — replaces the hidden YouTube wordmark. Top-left
          shows the SIHMT logo mark; bottom-right shows the same as a
          tasteful watermark instead of the covered YouTube wordmark. */}
      <div className="pointer-events-none absolute left-3 top-2 z-[18] flex items-center gap-2 text-white"
           data-testid="yt-brand-top">
        <img src="/sihmt-logo.png" alt="SIHMT"
             className="h-7 w-auto rounded bg-white/95 p-0.5 shadow" />
        <span className="text-[11px] font-bold uppercase tracking-widest text-white/90">Steward LMS</span>
      </div>
      <div className="pointer-events-none absolute right-3 bottom-2 z-[22] flex items-center gap-1.5 opacity-80"
           data-testid="yt-brand-bottom">
        <img src="/sihmt-logo.png" alt="SIHMT"
             className="h-5 w-auto rounded bg-white/95 p-0.5" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-white/90">SIHMT</span>
      </div>

      {/* Steward-logo + learner watermarks (reuse from parent props) */}
      {watermarks}

      {/* Progress overlay top-left */}
      <div className="pointer-events-none absolute left-2 top-2 z-20 rounded bg-black/50 px-2 py-1 text-[10px] font-bold text-white/90 backdrop-blur"
           data-testid="yt-progress-indicator">
        <span className="text-white/60">Progress: </span>{watchedPct.toFixed(0)}%
        <span className="text-white/50"> / {Number(payload?.security?.minimum_watch_percentage || 99)}%</span>
        {completedRef.current && <span className="ml-2 rounded bg-emerald-500/90 px-1.5 py-0.5 text-[9px]">COMPLETED</span>}
      </div>

      {/* Custom controls bar */}
      <div className="absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/80 to-transparent p-2 sm:p-3" data-testid="yt-custom-controls">
        <div className="flex items-center gap-2 text-white">
          <button type="button" onClick={togglePlay} data-testid="yt-toggle-play"
                  className="rounded-full bg-white/20 p-1.5 hover:bg-white/30">
            {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          </button>
          <span className="min-w-[80px] font-mono text-[10px] tabular-nums text-white/80">
            {fmt(currentTime)} / {fmt(duration)}
          </span>
          {/* Range input scrubber — anti-skip guard runs on change */}
          <input
            type="range"
            min={0}
            max={Math.max(1, duration)}
            step={0.5}
            value={Math.min(currentTime, duration || 0)}
            onChange={onScrub}
            data-testid="yt-scrubber"
            aria-label="Video position"
            className="h-1 flex-1 cursor-pointer accent-emerald-500"
            // Visual cue: max-watched marker (rewind zone)
            style={{
              background: duration
                ? `linear-gradient(to right, rgba(16,185,129,0.6) 0%, rgba(16,185,129,0.6) ${(maxWatchedRef.current / duration) * 100}%, rgba(255,255,255,0.15) ${(maxWatchedRef.current / duration) * 100}%, rgba(255,255,255,0.15) 100%)`
                : undefined,
            }}
          />
          <button type="button" onClick={toggleMute} data-testid="yt-toggle-mute"
                  className="rounded-full bg-white/20 p-1.5 hover:bg-white/30">
            {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
          </button>
          <button type="button" data-testid="yt-toggle-cc"
                  title={capsOn ? "Hide subtitles" : "Show subtitles"}
                  onClick={() => {
                    const p = playerRef.current;
                    if (!p) return;
                    try {
                      if (capsOn) { p.unloadModule("captions"); p.unloadModule("cc"); }
                      else { p.loadModule("captions"); p.loadModule("cc"); p.setOption("captions", "track", { languageCode: "en" }); }
                      setCapsOn(v => !v);
                    } catch (_e) { /* module may not be available for this video */ }
                  }}
                  className={`rounded-full p-1.5 hover:bg-white/30 ${capsOn ? "bg-emerald-500/70" : "bg-white/20"}`}>
            {capsOn ? <Captions className="h-3.5 w-3.5" /> : <CaptionsOff className="h-3.5 w-3.5" />}
          </button>
          <button type="button" onClick={goFullscreen} data-testid="yt-fullscreen"
                  className="rounded-full bg-white/20 p-1.5 hover:bg-white/30">
            <Maximize className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Loading spinner overlay until YT API + iframe are ready */}
      {!ready && (
        <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-black/60 text-white" data-testid="yt-loading">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          <span className="text-xs">Loading YouTube player…</span>
        </div>
      )}
      {resumed && (
        <div className="pointer-events-none absolute right-2 top-10 z-20 rounded bg-emerald-500/90 px-2 py-1 text-[9px] font-bold text-white"
             data-testid="yt-resumed-badge">
          RESUMED
        </div>
      )}

      {/* Manual "Mark as complete" — same 95 % / 100 % semantics as the LMS
          <video> player: unlocks when server flips can_mark_complete=true;
          system still auto-completes at ≥ 99 % on its own. */}
      {canMarkComplete && !completedRef.current && (
        <div className="pointer-events-auto absolute right-2 bottom-14 z-20 sm:right-3 sm:bottom-16" data-testid="video-mark-complete-container">
          <button
            type="button"
            onClick={handleMarkComplete}
            disabled={markingComplete}
            data-testid="video-mark-complete-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1.5 text-[11px] font-bold text-white shadow-lg ring-2 ring-white/30 transition hover:bg-emerald-600 disabled:opacity-60"
          >
            {markingComplete ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <CheckCircle2 className="h-3 w-3" />
            )}
            Mark as complete
          </button>
        </div>
      )}
    </div>
  );
}
